import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { transportProviderAPI } from '../../services/api';
import { useTransportProviderAuth } from '../../context/TransportProviderAuthContext';

// Full label set — kept as one map (not two) since a vehicle already saved
// under an old/other-market category still needs a real label to display,
// even once it's no longer offered as an option to newly-added ones.
const VEHICLE_LABELS = {
  rickshaw: 'ركشة', saloon: 'صالون', fourwheel: 'فوريل', box: 'بوكس', bus: 'حافلة',
  pickup: 'بيكب', light_truck: 'دفار', truck_5ton: 'شاحنة 5 طن', truck_10ton: 'شاحنة 10 طن',
  heavy_truck: 'شاحنة ثقيلة', trailer: 'تريلا', container_carrier: 'ناقلة اليات',
  motorcycle: 'دراجة نارية', taxi: 'تاكسي', van: 'فان',
};

// What's actually offered as a fleet option depends on which market(s) the
// provider registered for — a مشاوير-only provider has no reason to see
// truck categories, and vice versa. rickshaw/bus are legitimately useful in
// either market, so they appear in both lists rather than picking a side.
const CARGO_KEYS = ['rickshaw', 'pickup', 'bus', 'light_truck', 'truck_5ton', 'truck_10ton', 'heavy_truck', 'trailer', 'container_carrier'];
const RIDE_KEYS = ['rickshaw', 'saloon', 'fourwheel', 'box', 'bus'];

const inputCls = 'w-full border-2 border-gray-200 hover:border-blue-400/40 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 rounded-xl px-4 py-2.5 text-sm transition-all outline-none';
const labelCls = 'block text-xs font-bold text-gray-600 mb-1.5';

const TransportProviderVehiclesPage = () => {
  const { session } = useTransportProviderAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = adding a new vehicle; otherwise the vehicle being edited
  const [category, setCategory] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [model, setModel]       = useState('');
  const [capacityKg, setCapacityKg] = useState('');
  const [driverName, setDriverName]   = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [saving, setSaving]     = useState(false);

  // !== false so a session predating servicesCargo/servicesRides (or one
  // where it hasn't loaded yet) fails open to "show everything" rather than
  // silently offering no categories at all.
  const availableKeys = useMemo(() => {
    const cargo = session?.provider?.servicesCargo !== false;
    const rides = session?.provider?.servicesRides !== false;
    const keys = new Set([...(cargo ? CARGO_KEYS : []), ...(rides ? RIDE_KEYS : [])]);
    return Object.keys(VEHICLE_LABELS).filter((k) => keys.has(k));
  }, [session]);

  useEffect(() => {
    if (availableKeys.length > 0 && !availableKeys.includes(category)) {
      setCategory(availableKeys[0]);
    }
  }, [availableKeys, category]);

  const load = () => {
    setLoading(true);
    transportProviderAPI.getVehicles()
      .then((res) => setVehicles(res.data.data || []))
      .catch(() => toast.error('فشل في تحميل الأسطول'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditingId(null); setPlateNumber(''); setModel(''); setCapacityKg('');
    setDriverName(''); setDriverPhone('');
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setCategory(v.category);
    setPlateNumber(v.plateNumber || '');
    setModel(v.model || '');
    setCapacityKg(v.capacityKg || '');
    setDriverName(v.driverName || '');
    setDriverPhone(v.driverPhone || '');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!driverName || !driverPhone) { toast.error('اسم ورقم هاتف السائق مطلوبان'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await transportProviderAPI.updateVehicle(editingId, { plateNumber, model, capacityKg: capacityKg || undefined, driverName, driverPhone });
        toast.success('تم تحديث بيانات المركبة');
      } else {
        await transportProviderAPI.addVehicle({ category, plateNumber, model, capacityKg: capacityKg || undefined, driverName, driverPhone });
        toast.success('تمت إضافة المركبة');
      }
      setShowForm(false); resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّرت العملية');
    } finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try {
      await transportProviderAPI.toggleVehicle(id);
      load();
    } catch {
      toast.error('تعذّر التحديث');
    }
  };

  return (
    <div dir="rtl" className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-black text-gray-800">🚛 أسطولي</h1>
        <button
          onClick={() => { if (showForm) { resetForm(); } setShowForm((s) => !s); }}
          className="bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold px-4 py-2 rounded-xl transition"
        >
          {showForm ? 'إلغاء' : '+ إضافة مركبة'}
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4 -mt-4">
        اسم ورقم هاتف السائق يُسجَّلان هنا مرة واحدة لكل مركبة، ثم يُختاران من القائمة عند تقديم عرض أو تعيين مركبة — بدون كتابة حرة.
      </p>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3 mb-6">
          <div>
            <label className={labelCls}>نوع المركبة *</label>
            <select className={inputCls} value={category} disabled={!!editingId} onChange={(e) => setCategory(e.target.value)}>
              {availableKeys.map((k) => <option key={k} value={k}>{VEHICLE_LABELS[k]}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className={labelCls}>رقم اللوحة</label><input className={inputCls} value={plateNumber} onChange={(e) => setPlateNumber(e.target.value)} /></div>
            <div><label className={labelCls}>الموديل</label><input className={inputCls} value={model} onChange={(e) => setModel(e.target.value)} /></div>
          </div>
          <div><label className={labelCls}>السعة القصوى (كجم)</label><input type="number" className={inputCls} value={capacityKg} onChange={(e) => setCapacityKg(e.target.value)} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-100">
            <div><label className={labelCls}>اسم السائق *</label><input className={inputCls} value={driverName} onChange={(e) => setDriverName(e.target.value)} required /></div>
            <div><label className={labelCls}>هاتف السائق *</label><input dir="ltr" className={inputCls} value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} required /></div>
          </div>
          <button type="submit" disabled={saving} className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white py-2.5 rounded-xl font-bold transition">
            {saving ? '...جارٍ الحفظ' : (editingId ? 'حفظ التعديلات' : 'إضافة')}
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-center text-gray-400 py-10">جاري التحميل...</div>
      ) : vehicles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400">لا توجد مركبات مسجلة بعد</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {vehicles.map((v) => (
            <div key={v.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-bold text-gray-800 text-sm">{VEHICLE_LABELS[v.category] || v.category}</p>
                <p className="text-xs text-gray-500">{v.plateNumber || '—'} {v.model ? `· ${v.model}` : ''}</p>
                {v.driverName ? (
                  <p className="text-xs text-gray-500">👤 {v.driverName} — {v.driverPhone}</p>
                ) : (
                  <p className="text-xs text-red-600 font-semibold">⚠️ بيانات السائق غير مكتملة — لا يمكن استخدام هذه المركبة في عرض جديد</p>
                )}
              </div>
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <button onClick={() => startEdit(v)} className="text-xs font-bold px-3 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition">
                  تعديل
                </button>
                <button
                  onClick={() => toggle(v.id)}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition ${v.isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}
                >
                  {v.isActive ? 'نشطة' : 'موقوفة'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransportProviderVehiclesPage;
